import React, { useState } from 'react';
import { CheckCircle, Loader2, X } from 'lucide-react';
import { BUSINESS, RDV_CONSEIL_URL, createQuoteRequest } from '../config';
import { recordProductView, writeJourneyCookie } from '../hooks/useBrowseJournal';

export default function QuoteRequestModal({ product, onClose }) {
  const defaultMessage = `Je suis intéressé(e) par ${product.name}${product.external_ref ? ` - Réf ${product.external_ref}` : ''}`;
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: defaultMessage });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await createQuoteRequest({
        product_id: product.id,
        product_name: product.name,
        external_ref: product.external_ref || '',
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-sm border border-slate-200 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="font-heading font-semibold text-lg text-primary uppercase tracking-wider">
            {success ? 'Demande envoyee' : 'Demander un devis'}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/5 transition-colors">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h4 className="font-heading font-semibold text-xl text-primary uppercase tracking-wider mb-2">Demande envoyee</h4>
            <p className="text-slate-700 text-sm mb-6">
              Nous avons bien recu votre demande de devis pour <strong className="text-primary">{product.name}</strong>. Nous vous recontacterons dans les meilleurs delais.
            </p>
            <a
              href={RDV_CONSEIL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                recordProductView({ id: product.id, name: product.name, price: product.price });
                writeJourneyCookie();
              }}
              className="block w-full bg-secondary text-white font-bold uppercase tracking-wider rounded-sm py-3 text-sm text-center hover:opacity-90 transition-opacity mb-3"
              data-testid="quote-success-rdv-link"
            >
              Choisir mon créneau de rappel (gratuit, 10 min)
            </a>
            <div className="flex gap-3">
              <a
                href={`tel:${BUSINESS.telephoneRaw}`}
                className="flex-1 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3 text-sm text-center transition-colors"
              >
                Appeler la boutique
              </a>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-slate-100 text-slate-700 border border-slate-200 font-bold uppercase tracking-wider rounded-sm py-3 text-sm hover:bg-slate-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="bg-white/5 rounded-sm p-3 flex items-center gap-3">
              <div className="w-12 h-12 rounded-sm bg-secondary/10 flex items-center justify-center shrink-0">
                <span className="text-secondary font-bold text-sm">{product.price?.toFixed(0)}EUR</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-primary line-clamp-1">{product.name}</p>
                {product.external_ref && (
                  <p className="text-xs text-slate-600">Ref : {product.external_ref}</p>
                )}
              </div>
            </div>

            <label className="space-y-1.5 block">
              <span className="block text-xs text-slate-600">Nom complet</span>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                placeholder="Jean Dupont"
              />
            </label>

            <label className="space-y-1.5 block">
              <span className="block text-xs text-slate-600">Email</span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                placeholder="jean.dupont@email.fr"
              />
            </label>

            <label className="space-y-1.5 block">
              <span className="block text-xs text-slate-600">Telephone</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                placeholder="06 12 34 56 78"
              />
            </label>

            <label className="space-y-1.5 block">
              <span className="block text-xs text-slate-600">Message</span>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary resize-none"
              />
            </label>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <p className="text-[10px] text-slate-500 leading-relaxed">
              Donnees utilisees uniquement pour traiter votre demande.{' '}
              <a href="/confidentialite" className="text-secondary/80 hover:text-secondary">En savoir plus</a>
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                'Envoyer la demande de devis'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
